/**
 * Maps NPC generator output to Supabase database insert payloads.
 * Principle: generator is source of truth. Values stored as-is, no lossy mapping.
 */

import type { GeneratorResult, GeneratorParams } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type R = any // shorthand for loosely-typed generator sub-fields (family, contacts, history)

export function mapCharacterRow(
  npc: GeneratorResult,
  campaignId: string,
  gameYear: number = 720,
  shadowParams?: GeneratorParams | Record<string, unknown> | null
) {
  // v3.3.0: aging data lives in npc.aging, with shortcuts at top level
  const aging = (npc.aging ?? npc) as R
  const attrs = npc.attributes ?? aging.attributes ?? {}
  const pp = aging.physicalProfile ?? {}
  const birthYear = aging._birthYear ?? (gameYear - (npc.age ?? 30))
  const sp = (shadowParams ?? {}) as Record<string, R>

  return {
    campaign_id: campaignId,
    character_role: 'npc' as const,
    creation_method: 'automatic' as const,
    name: npc.name ?? 'Unnamed NPC',
    species_id: 'Human', // generator is human-only currently
    sex: npc.sex === 'female' ? 'Female' : 'Male',
    social_class: npc.socialClass ?? 'unguilded',
    birth_year: birthYear,
    birth_month: monthNameToNumber(npc.birthMonth) ?? 1,
    birth_day: npc.birthDay ?? 1,
    sunsign_id: mapSunsign(npc.sunsign),
    on_cusp: npc.sunsigns?.cusp ?? false,
    culture_id: mapCulture(npc),
    occupation_name: aging.occupationLabel ?? mapOccupation(typeof npc.occupation === 'string' ? npc.occupation
      : npc.occupation?.label ?? npc.occupation?.id ?? aging.occupation ?? null),
    // Attributes
    attr_str: attrs.STR ?? 10,
    attr_sta: attrs.STA ?? 10,
    attr_dex: attrs.DEX ?? 10,
    attr_agl: attrs.AGL ?? 10,
    attr_eye: attrs.EYE ?? 10,
    attr_hrg: attrs.HRG ?? 10,
    attr_sml: attrs.SML ?? 10,
    attr_voi: attrs.VOI ?? 10,
    attr_int: attrs.INT ?? 10,
    attr_aur: attrs.AUR ?? 10,
    attr_wil: attrs.WIL ?? 10,
    morality: npc.morality ?? 10,
    comeliness: attrs.CML ?? 10,
    // OCEAN
    ocean_o: npc.oceanScores?.O ?? aging.oceanScores?.O ?? null,
    ocean_c: npc.oceanScores?.C ?? aging.oceanScores?.C ?? null,
    ocean_e: npc.oceanScores?.E ?? aging.oceanScores?.E ?? null,
    ocean_a: npc.oceanScores?.A ?? aging.oceanScores?.A ?? null,
    ocean_n: npc.oceanScores?.N ?? aging.oceanScores?.N ?? null,
    ocean_archetype: npc.archetype?.id ?? aging.archetype?.id ?? null,
    // Religion
    deity: npc.publicDeity ?? aging.publicDeity ?? null,
    piety: npc.piety ?? aging.piety ?? 0,
    secret_deity: npc.secretDeity ?? aging.secretDeity ?? null,
    is_secret_worshipper: aging.isSecretWorshipper ?? false,
    // Physical — prefer physicalProfile, fall back to attributes
    height_inches: pp.heightInches ?? attrs._heightIn ?? null,
    frame: pp.frame ?? attrs._frame ?? null,
    weight_lbs: pp.weightLbs ?? attrs._weightLbs ?? null,
    complexion: pp.complexion ?? null,
    hair_color: pp.hairColour ?? null,
    eye_color: pp.eyeColour ?? null,
    // Extended physical (JSONB)
    physical_profile: pp ? {
      hairLength: pp.hairLength,
      marks: pp.marks,
      apparentAge: pp.apparentAge,
      appearance: pp.appearance,
      heightBand: pp.heightBand,
    } : null,
    // Generator enrichment
    personality_text: aging.personality ?? null,
    life_phase: aging.phase ?? null,
    archetype_id: npc.archetype?.id ?? aging.archetype?.id ?? null,
    archetype_label: npc.archetype?.label ?? aging.archetype?.label ?? null,
    wealth_score: aging.wealthScore ?? null,
    wealth_level: npc.wealthLevel ?? aging.wealthLevel ?? null,
    // Equipment & appearances (JSONB) — v3.3.0 top-level subsystems
    equipment_loadouts: npc.equipment ? {
      ...npc.equipment,
      encumbrance: aging.encumbrance ?? npc.encumbrance ?? null,
    } : null,
    appearances: (npc.appearance ?? aging.appearances)
      ? { ...(npc.appearance ?? aging.appearances), _source: (npc.appearance ?? aging.appearances)?._source ?? 'template' }
      : null,
    // Conditions
    active_conditions: npc.conditions ?? aging.conditions ?? [],
    funds_denarii: (() => {
      // Starting funds based on wealth level — coin on hand, not total worth
      const level = aging.wealthLevel ?? npc.wealthLevel ?? 'poor'
      const WEALTH_FUNDS: Record<string, number> = {
        destitute: 0, poor: 5, low: 15, average: 40, comfortable: 80,
        prosperous: 150, wealthy: 300, rich: 600,
      }
      return WEALTH_FUNDS[level] ?? 20
    })(),
    // Family summary
    sibling_rank: npc.birthOrder ? `${npc.birthOrder} of ${npc.familySize ?? npc.siblingCount ?? 1}` : null,
    family_size: npc.familySize ?? null,
    estrangement: npc.estrangementBand ?? null,
    clanhead: npc.clanhead ? 'Yes' : null,
    // Extra fields from generator
    sexuality: aging.sexuality ?? null,
    medical_traits: aging.medicalTrait ?? [],
    upbringing_type: aging.upbringing ?? null,
    parent_class: aging.parentClass ?? null,
    parent_occupation: aging.parentOccupation ?? null,
    hobby_skill: aging.hobbySkill ?? null,
    birth_settlement: aging.birthSettlement ?? null,
    current_settlement: aging.currentSettlement ?? aging.settlementType ?? null,
    narrative: aging.narrative ?? [],
    // GM data
    gm_notes: npc.secretCallout ?? null,
    secrets: npc.isSecretWorshipper
      ? `Secret worshipper of ${npc.secretDeity}${npc.tashalCell ? ' (Tashal cell)' : ''}`
      : null,
    // Generator metadata — seed + params needed to reproduce this character
    generator_seed: {
      seed: aging.seed ?? (shadowParams as Record<string, unknown>)?.seed ?? null,
      version: '3.18.1',
      socialClass: npc.socialClass ?? null,
      sex: npc.sex ?? null,
      targetAge: npc.age ?? null,
      importance: aging.importance ?? 'regular',
      culture: aging.culture ?? null,
      occupation: typeof npc.occupation === 'object' ? npc.occupation?.id : npc.occupation ?? null,
      // Constraint params — needed to reproduce constrained NPCs
      ...(sp?.maritalStatus ? { maritalStatus: sp.maritalStatus } : {}),
      ...(sp?.childrenAlive != null ? { childrenAlive: sp.childrenAlive } : {}),
      ...(sp?.childrenTotal != null ? { childrenTotal: sp.childrenTotal } : {}),
      ...(sp?.requiredConditions?.length ? { requiredConditions: sp.requiredConditions } : {}),
      ...(sp?.forbiddenConditions?.length ? { forbiddenConditions: sp.forbiddenConditions } : {}),
      ...(sp?.requiredEvents?.length ? { requiredEvents: sp.requiredEvents } : {}),
      ...(sp?.publicDeity ? { publicDeity: sp.publicDeity } : {}),
      ...(sp?.hobbySkill ? { hobbySkill: sp.hobbySkill } : {}),
      ...(sp?.location ? { location: sp.location } : {}),
    },
    generator_raw: null,  // No longer stored — reproducible from generator_seed + shadow_params
    shadow_params: shadowParams ?? null,
  }
}

export function mapFamilyMembers(npc: GeneratorResult, characterId: string) {
  const aging = (npc.aging ?? npc) as R
  const members: R[] = []
  let order = 0
  const birthYear = aging._birthYear ?? 690

  // Extract death years from history for family members
  const history: R[] = npc.history ?? aging.history ?? []
  const parentDeathEvents = history.filter((h: R) => h.eventId === 'death_of_parent')
  const spouseDeathEvents = history.filter((h: R) => h.eventId === 'spouse_dies')

  // v3.3.0: unified family array with _familyGroup field
  const roleLabels: Record<string, string> = {
    father: 'Father', mother: 'Mother',
    foster_father: 'Foster Father', foster_mother: 'Foster Mother',
    stepfather: 'Stepfather', stepmother: 'Stepmother',
  }
  const familyArray = aging.family ?? []

  // Track which death events we've used
  let parentDeathIdx = 0
  let spouseDeathIdx = 0

  for (const f of familyArray) {
    const group = f._familyGroup ?? 'contact'
    if (!['parent', 'sibling', 'spouse', 'child'].includes(group)) continue
    const label = group === 'parent' && f.role
      ? `${f.name ?? 'Unknown'} (${roleLabels[f.role] ?? f.role})`
      : null

    // Enrich stub_data with death info
    const enrichedStub = { ...f }
    if (f.status === 'deceased' && !f.diedAge) {
      if (group === 'parent' && parentDeathIdx < parentDeathEvents.length) {
        const evt = parentDeathEvents[parentDeathIdx++]
        enrichedStub.deathYear = evt.year ?? (birthYear + evt.age)
        enrichedStub.diedAge = f.birthYear ? (enrichedStub.deathYear - f.birthYear) : null
      } else if (group === 'spouse' && spouseDeathIdx < spouseDeathEvents.length) {
        const evt = spouseDeathEvents[spouseDeathIdx++]
        enrichedStub.deathYear = evt.year ?? (birthYear + evt.age)
        enrichedStub.diedAge = f.birthYear ? (enrichedStub.deathYear - f.birthYear) : null
      } else if (group === 'sibling' && f.birthYear) {
        // No explicit death event for siblings — estimate: died between birth and game year
        const maxAge = (aging.gameYear ?? 720) - f.birthYear
        const estimatedAge = Math.max(1, Math.floor(maxAge * 0.4 + Math.random() * maxAge * 0.4))
        enrichedStub.deathYear = f.birthYear + estimatedAge
        enrichedStub.diedAge = estimatedAge
        enrichedStub.deathEstimated = true
      }
    } else if (f.status === 'deceased' && f.diedAge != null && f.birthYear) {
      enrichedStub.deathYear = f.birthYear + f.diedAge
    }

    // Realistic mortality: living parents/foster parents over ~70 should be deceased
    // HârnMaster life expectancy is ~55-65; surviving past 70 is rare
    let memberStatus = f.status ?? 'alive'
    const gameYear = aging.gameYear ?? 720
    if (memberStatus === 'alive' && group === 'parent' && f.birthYear) {
      const wouldBeAge = gameYear - f.birthYear
      if (wouldBeAge > 70) {
        memberStatus = 'deceased'
        // Estimate death between 55-70
        const deathAge = 55 + Math.floor((f.birthYear % 16)) // deterministic from birthYear
        enrichedStub.diedAge = deathAge
        enrichedStub.deathYear = f.birthYear + deathAge
        enrichedStub.deathEstimated = true
      }
    }

    members.push({
      character_id: characterId,
      relationship: group,
      stub_data: enrichedStub,
      member_name: label ?? f.name ?? f.role ?? group,
      member_sex: f.sex ?? null,
      member_status: memberStatus,
      member_birth_year: f.birthYear ?? null,
      stub_seed: clampSeed(f.stubSeed),
      marriage_year: group === 'spouse' && f.marriedAtPrincipalAge
        ? birthYear + f.marriedAtPrincipalAge : null,
      born_at_principal_age: group === 'child' ? (f.bornAtPrincipalAge ?? null) : null,
      sort_order: order++,
    })
  }

  // Fallback: v2.x-style separate arrays if family[] is empty
  if (familyArray.length === 0) {
    for (const p of aging.parents ?? []) {
      members.push({ character_id: characterId, relationship: 'parent', stub_data: p,
        member_name: p.name ?? p.role ?? 'Parent', member_sex: p.sex ?? null,
        member_status: p.status ?? 'alive', member_birth_year: p.birthYear ?? null,
        stub_seed: clampSeed(p.stubSeed), sort_order: order++ })
    }
    for (const s of aging.siblings ?? []) {
      members.push({ character_id: characterId, relationship: 'sibling', stub_data: s,
        member_name: s.name ?? 'Sibling', member_sex: s.sex ?? null,
        member_status: s.status ?? 'alive', member_birth_year: s.birthYear ?? null,
        stub_seed: clampSeed(s.stubSeed), sort_order: order++ })
    }
    for (const sp of npc.spouses ?? aging.spouses ?? []) {
      members.push({ character_id: characterId, relationship: 'spouse', stub_data: sp,
        member_name: sp.name ?? 'Spouse', member_sex: sp.sex ?? null,
        member_status: sp.status ?? 'alive', member_birth_year: sp.birthYear ?? null,
        stub_seed: clampSeed(sp.stubSeed),
        marriage_year: sp.marriedAtPrincipalAge ? birthYear + sp.marriedAtPrincipalAge : null,
        sort_order: order++ })
    }
    for (const c of npc.children ?? aging.children ?? []) {
      members.push({ character_id: characterId, relationship: 'child', stub_data: c,
        member_name: c.name ?? 'Child', member_sex: c.sex ?? null,
        member_status: c.status ?? 'alive',
        member_birth_year: c.birthYear ?? (c.bornAtPrincipalAge ? birthYear + c.bornAtPrincipalAge : null),
        stub_seed: clampSeed(c.stubSeed), born_at_principal_age: c.bornAtPrincipalAge ?? null,
        sort_order: order++ })
    }
  }

  // v3.2.1: household staff
  for (const h of aging.household ?? []) {
    members.push({
      character_id: characterId,
      relationship: 'household' as unknown as string, // extend CHECK later if needed
      stub_data: h,
      member_name: h.name ?? h.role ?? 'Staff',
      member_sex: h.sex ?? null,
      member_status: h.status ?? 'alive',
      member_birth_year: h.birthYear ?? null,
      stub_seed: clampSeed(h.stubSeed),
      sort_order: order++,
    })
  }

  return members
}

export function mapContacts(npc: GeneratorResult, characterId: string) {
  const aging = (npc.aging ?? npc) as R
  return (npc.contacts ?? aging.contacts ?? []).map((c: R) => {
    // Build a rich description from generator data
    const parts: string[] = []
    if (c.sex) parts.push(c.sex === 'male' ? 'Male' : 'Female')
    if (c.socialClass) parts.push(c.socialClass)
    if (c.contactType && c.contactType !== c.role) parts.push(c.contactType)
    const identity = parts.length > 0 ? parts.join(', ') + '.' : ''

    const context: string[] = []
    if (c.metAtPrincipalAge) context.push(`Met at age ${c.metAtPrincipalAge}`)
    if (c.ageAtMeeting) context.push(`contact was ${c.ageAtMeeting} at the time`)
    const meeting = context.length > 0 ? ' ' + context.join('; ') + '.' : ''

    const status = c.status === 'deceased' ? ' Deceased.' : ''
    const note = c.note ? ' ' + c.note : ''

    const gmNotes = (identity + meeting + note + status).trim()

    return {
      character_id: characterId,
      contact_type: c.role ?? c.contactType ?? 'contact',
      contact_name: c.name ?? 'Unknown',
      relationship: c.relationshipBand ?? 'neutral',
      loyalty: c.loyaltyScore ?? 50,
      is_best_friend: c.bestFriend ?? false,
      gm_notes: gmNotes || null,
      is_revealed: false,
    }
  })
}

export function mapBiography(npc: GeneratorResult, characterId: string) {
  const aging = (npc.aging ?? npc) as R
  return (npc.history ?? aging.history ?? [])
    .filter((h: R) => h.eventId !== 'quiet_year') // skip uneventful years
    .map((h: R, i: number) => ({
      character_id: characterId,
      event_label: h.eventLabel ?? h.label ?? h.eventId ?? 'Event',
      flavour_text: h.flavourNote ?? null,
      character_age: h.age ?? 0,
      game_year: h.year ?? null,
      is_gm_only: false,
      effects_applied: h.effects ?? {},
      sequence: i,
    }))
}

// ─── Equipment item mapping — extracts individual items from generator output ──

// Items that should be on the belt, not in the backpack
const BELT_ITEMS = /knife|dagger|pouch|purse|waterskin|wineskin|flask/i
// Items that are containers
const CONTAINER_ITEMS = /backpack|saddlebag|belt pouch|pouch \(|quiver|sack|chest|barrel/i

function parseQty(name: string): { cleanName: string; qty: number } {
  const m = name.match(/\s*[×x](\d+)/)
  return {
    cleanName: name
      .replace(/\s*[×x]\d+.*$/, '')       // "Arrows ×12 (broadhead)" → "Arrows"
      .replace(/\s*\(\d+\s*lb\)/, '')     // "Backpack (30 lb)" → "Backpack"
      .replace(/\s*\(\d+\)/, '')          // "Quiver (12)" → "Quiver"
      .replace(/\s*\([^)]*\)$/, '')       // "Arrows (hunting broadhead)" → "Arrows" (trailing parens)
      .trim(),
    qty: m ? parseInt(m[1]) : 1,
  }
}

export function mapEquipmentItems(npc: GeneratorResult, characterId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const eq = (npc.equipment ?? (npc.aging as any)?.equipment) as Record<string, any> | null
  if (!eq) return []

  const rows: Array<{
    character_id: string
    item_name: string
    quantity: number
    weight_lbs: number | null
    value_denarii: number | null
    location: string
    is_carried: boolean
    notes: string | null
    _containerName?: string  // post-processing: put this item inside the named container
  }> = []

  const add = (name: string, opts?: { qty?: number; weight?: number; value?: number; location?: string; notes?: string; containerName?: string }) => {
    rows.push({
      character_id: characterId,
      item_name: name,
      quantity: opts?.qty ?? 1,
      weight_lbs: opts?.weight ?? null,
      value_denarii: opts?.value ?? null,
      location: opts?.location ?? 'backpack',
      is_carried: (opts?.location ?? 'backpack') !== 'cached',
      notes: opts?.notes ?? null,
      _containerName: opts?.containerName,
    })
  }

  // Clothing, weapons, armour, shields are NOT added here.
  // They go to their dedicated tables:
  //   Clothing + armour → character_armour (via mapArmour)
  //   Weapons + shields → character_weapons (via mapWeapons)
  // Adding them here would double-count weight in load calculation.

  // Jewellery — worn
  const jewellery = eq.jewellery?.items as Array<{ name: string; cost_d?: number; weight_oz?: number }> | undefined
  if (jewellery) for (const j of jewellery) {
    add(j.name, { weight: j.weight_oz ? j.weight_oz / 16 : undefined, value: j.cost_d, location: 'worn' })
  }

  // Items that belong inside a quiver
  const QUIVER_ITEMS = /arrow|quarrel|bolt/i

  // Profession tools — belt for small tools, ammunition in quiver
  const tools = eq.profession_tools?.items as Array<{ name: string; cost_d?: number; weight_lb?: number }> | undefined
  if (tools) for (const t of tools) {
    const { cleanName, qty } = parseQty(t.name)
    // Generator weight/cost is total for the stack — divide by qty for per-unit
    const unitWeight = (t.weight_lb && qty > 1) ? t.weight_lb / qty : t.weight_lb
    const unitValue = (t.cost_d && qty > 1) ? Math.round(t.cost_d / qty) : t.cost_d
    const inQuiver = QUIVER_ITEMS.test(cleanName)
    add(cleanName, { qty, weight: unitWeight, value: unitValue, location: inQuiver ? 'worn' : 'belt', containerName: inQuiver ? 'Quiver' : undefined })
  }

  // Miscellaneous items — smart location: containers and belt items detected
  const misc = eq.miscellaneous?.items as Array<{ name: string; cost_d?: number; weight_lb?: number }> | undefined
  if (misc) for (const m of misc) {
    const { cleanName, qty } = parseQty(m.name)
    const unitWeight = (m.weight_lb && qty > 1) ? m.weight_lb / qty : m.weight_lb
    const unitValue = (m.cost_d && qty > 1) ? Math.round(m.cost_d / qty) : m.cost_d
    if (CONTAINER_ITEMS.test(cleanName)) {
      // Containers go on belt (pouches) or worn (backpacks, saddlebags)
      const loc = /pouch|purse/i.test(cleanName) ? 'belt' : 'worn'
      add(cleanName, { qty, weight: unitWeight, value: unitValue, location: loc })
    } else if (BELT_ITEMS.test(cleanName)) {
      add(cleanName, { qty, weight: unitWeight, value: unitValue, location: 'belt' })
    } else {
      // Regular items go in the backpack container
      add(cleanName, { qty, weight: unitWeight, value: unitValue, location: 'worn', containerName: 'Backpack' })
    }
  }

  // Personal items — in backpack
  const personal = eq.personal_items?.items as Array<{ name: string; cost_d?: number; sentimental?: boolean }> | undefined
  if (personal) for (const p of personal) {
    add(p.name, { value: p.cost_d, location: 'worn', containerName: 'Backpack', notes: p.sentimental ? 'personal' : undefined })
  }

  return rows
}

// ─── Weapon mapping — extracts weapons from generator combat_gear ─────────

function parseImpact(impact?: string): { b: number; e: number; p: number } {
  if (!impact) return { b: 0, e: 0, p: 0 }
  const bm = impact.match(/B(\d+)/); const em = impact.match(/E(\d+)/); const pm = impact.match(/P(\d+)/)
  return { b: bm ? parseInt(bm[1]) : 0, e: em ? parseInt(em[1]) : 0, p: pm ? parseInt(pm[1]) : 0 }
}

export function mapWeapons(npc: GeneratorResult, characterId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const eq = (npc.equipment ?? (npc.aging as any)?.equipment) as Record<string, any> | null
  if (!eq?.combat_gear) return []

  const rows: Array<Record<string, unknown>> = []
  const aging = (npc.aging ?? npc) as R
  const skillRoster = aging.skillRoster as Array<{ name: string; oml: number; sb: number }> | undefined

  // HM3_TO_REF_WEAPON removed in v3.13.3 — engine now outputs ref_weapon_name from JSON.
  // WEAPON_TO_SKILLS removed in v3.13.3 — engine now outputs weapon_group; roster has weaponGroup.

  /**
   * Find ML for a weapon. Uses the weapon's ref_name to search the skill roster,
   * then falls back to the weapon_group. No hardcoded lookup table needed.
   */
  function findML(weaponRefName: string | null, weaponGroup: string | null): number {
    if (!skillRoster) return 30
    // Try weapon name as specialty (Broadsword), then weapon group (Sword)
    const entry = (weaponRefName ? skillRoster.find(s => s.name === weaponRefName) : null)
      ?? (weaponGroup ? skillRoster.find(s => s.name === weaponGroup) : null)
    if (!entry) return 30
    const improvements = aging.skillImprovements ?? {}
    const opsSpent = improvements[entry.name] ?? 0
    return Math.min(entry.sb + 100, entry.oml + opsSpent)
  }

  // HM3_FALLBACK removed in v3.13.2 — engine now loads weapon stats from hm3-weapons.json.
  // Equipment output includes complete stats. Fallback defaults (?? 0) handle any gaps.

  // Hand slot assignment — which weapon/shield is in which hand
  type HandItem = { hm3_key: string | null; name: string; type: string } | null
  const hands = eq.combat_gear.hands as { right: HandItem; left: HandItem } | undefined

  // Weapons — engine now outputs ref_weapon_name and weapon_group from hm3-weapons.json
  const weapons = eq.combat_gear.weapons as Array<{ name: string; hm3_key?: string; ref_weapon_name?: string; weapon_group?: string; wq?: number; wac?: number; wdc?: number; hm?: number; two_handed?: boolean; impact?: string; weight_lb?: number; cost_d?: number; weight_class?: string }> | undefined
  if (weapons) for (const w of weapons) {
    const imp = parseImpact(w.impact)
    const heldIn = hands?.right?.hm3_key === w.hm3_key ? 'right'
                 : hands?.left?.hm3_key === w.hm3_key ? 'left'
                 : null
    rows.push({
      character_id: characterId,
      weapon_name: w.name,
      ref_weapon_name: w.ref_weapon_name ?? null,
      weapon_quality: w.wq ?? 10,
      ml: findML(w.ref_weapon_name ?? null, w.weapon_group ?? null),
      attack_class: w.wac ?? 0,
      defence_class: w.wdc ?? 0,
      hand_mode_penalty: w.hm ?? 0,
      impact_b: imp.b, impact_e: imp.e, impact_p: imp.p,
      weight_lbs: w.weight_lb ?? 0,
      weight_class: w.weight_class ?? 'average',
      held_in: heldIn,
      two_handed: w.two_handed ?? false,
      is_equipped: true,
      is_primary: false,
    })
  }

  // Ranged/missile weapons from combat_gear.ranged
  const rangedWeapons = eq.combat_gear.ranged as Array<{ name: string; hm3_key?: string; ref_weapon_name?: string; weapon_group?: string; wq?: number; wac?: number; wdc?: number; impact?: string; weight_lb?: number; cost_d?: number; notes?: string }> | undefined
  if (rangedWeapons) for (const w of rangedWeapons) {
    const imp = parseImpact(w.impact)
    // Ranged weapons use both hands; conventionally track as held in right
    const heldIn = hands?.right?.hm3_key === w.hm3_key ? 'right' : null
    rows.push({
      character_id: characterId,
      weapon_name: w.name,
      ref_weapon_name: w.ref_weapon_name ?? null,
      weapon_quality: w.wq ?? 10,
      ml: findML(w.ref_weapon_name ?? null, w.weapon_group ?? null),
      attack_class: w.wac ?? 5,
      defence_class: w.wdc ?? 5,
      hand_mode_penalty: 0,
      impact_b: imp.b, impact_e: imp.e, impact_p: imp.p,
      weight_lbs: w.weight_lb ?? 0,
      weight_class: 'average',
      held_in: heldIn,
      two_handed: true,
      is_equipped: true,
      is_primary: false,
      notes: w.notes ?? null,
    })
  }

  // Shields (stored as weapons in the DB) — HM3 shield bash values
  const SHIELD_BASH: Record<string, { wac: number; wdc: number; b: number; p: number }> = {
    buckler:               { wac: 5, wdc: 15, b: 1, p: 0 },
    round_shield:          { wac: 5, wdc: 20, b: 2, p: 0 },
    roundshield:           { wac: 5, wdc: 20, b: 2, p: 0 },
    kite_shield:           { wac: 5, wdc: 25, b: 3, p: 0 },
    knight_shield:         { wac: 5, wdc: 20, b: 2, p: 0 },
    tower_shield:          { wac: 5, wdc: 25, b: 3, p: 0 },
    // Spiked variants — adds point aspect
    spiked_buckler:        { wac: 5, wdc: 15, b: 1, p: 2 },
    spiked_roundshield:    { wac: 5, wdc: 20, b: 2, p: 3 },
    spiked_kite_shield:    { wac: 5, wdc: 25, b: 3, p: 3 },
    spiked_knight_shield:  { wac: 5, wdc: 20, b: 2, p: 3 },
  }
  const shields = eq.combat_gear.shields as Array<{ name: string; hm3_key: string; ref_weapon_name?: string; weapon_group?: string; wq: number; wdc: number; weight_lb: number; cost_d: number }> | undefined
  if (shields) for (const s of shields) {
    const bash = SHIELD_BASH[s.hm3_key] ?? { wac: 5, wdc: 20, b: 2, p: 0 }
    const heldIn = hands?.left?.hm3_key === s.hm3_key ? 'left'
                 : hands?.right?.hm3_key === s.hm3_key ? 'right'
                 : null
    rows.push({
      character_id: characterId,
      weapon_name: s.name,
      ref_weapon_name: s.ref_weapon_name ?? null,
      weapon_quality: s.wq ?? 10,
      ml: findML(s.ref_weapon_name ?? null, s.weapon_group ?? 'Shield'),
      attack_class: bash.wac,
      defence_class: bash.wdc,
      hand_mode_penalty: 0,
      impact_b: bash.b, impact_e: 0, impact_p: bash.p,
      weight_lbs: s.weight_lb ?? 0,
      weight_class: 'average',
      held_in: heldIn,
      two_handed: false,
      is_equipped: true,
      is_primary: false,
    })
  }

  // Stats lookup for occupation weapons — keyed by skill/specialty name.
  // Values from hm3-weapons.json (Combat Tables). Groups use their canonical weapon.
  // wac=attack class, wdc=defence class, b=blunt, e=edge, p=point.
  const WEAPON_STATS: Record<string, { wac: number; wdc: number; b: number; e: number; p: number }> = {
    // Dagger group
    'Knife':        { wac: 5,  wdc: 0,  b: 0, e: 1, p: 4 },
    'Dagger':       { wac: 5,  wdc: 5,  b: 1, e: 2, p: 5 },
    'Taburi':       { wac: 5,  wdc: 0,  b: 0, e: 0, p: 4 },
    'Keltan':       { wac: 5,  wdc: 10, b: 0, e: 0, p: 3 },
    // Sword group (values from HM3 Combat p.3)
    'Longknife':    { wac: 10, wdc: 15, b: 1, e: 3, p: 5 },
    'Shortsword':   { wac: 10, wdc: 5,  b: 2, e: 4, p: 5 },
    'Broadsword':   { wac: 15, wdc: 10, b: 3, e: 5, p: 3 },
    'Sword':        { wac: 15, wdc: 10, b: 3, e: 5, p: 3 },
    'Falchion':     { wac: 15, wdc: 5,  b: 4, e: 6, p: 1 },
    'Estoc':        { wac: 15, wdc: 10, b: 3, e: 0, p: 6 },
    'Bastard Sword':{ wac: 20, wdc: 10, b: 4, e: 7, p: 4 },
    'Battlesword':  { wac: 25, wdc: 10, b: 5, e: 8, p: 4 },
    'Mang':         { wac: 15, wdc: 10, b: 3, e: 6, p: 0 },
    // Club group
    'Stick':        { wac: 5,  wdc: 5,  b: 2, e: 0, p: 0 },
    'Club':         { wac: 15, wdc: 5,  b: 4, e: 0, p: 0 },
    'Mace':         { wac: 15, wdc: 5,  b: 6, e: 0, p: 0 },
    'Morningstar':  { wac: 20, wdc: 5,  b: 0, e: 0, p: 5 },
    // Axe group
    'Hatchet':      { wac: 5,  wdc: 5,  b: 3, e: 4, p: 0 },
    'Handaxe':      { wac: 10, wdc: 5,  b: 4, e: 6, p: 0 },
    'Axe':          { wac: 10, wdc: 5,  b: 4, e: 6, p: 0 },
    'Battleaxe':    { wac: 20, wdc: 10, b: 6, e: 9, p: 6 },
    'Warhammer':    { wac: 15, wdc: 5,  b: 6, e: 0, p: 5 },
    'Sickle':       { wac: 5,  wdc: 5,  b: 1, e: 4, p: 3 },
    'Shorkana':     { wac: 5,  wdc: 5,  b: 0, e: 1, p: 3 },
    // Flail group
    'Nachakas':     { wac: 15, wdc: 10, b: 4, e: 0, p: 0 },
    'Grainflail':   { wac: 20, wdc: 5,  b: 5, e: 0, p: 0 },
    'Ball & Chain': { wac: 20, wdc: 10, b: 8, e: 0, p: 0 },
    'Warflail':     { wac: 25, wdc: 10, b: 9, e: 0, p: 0 },
    'Flail':        { wac: 20, wdc: 10, b: 8, e: 0, p: 0 },
    // Spear group (canonical = Spear 6')
    'Staff':        { wac: 20, wdc: 15, b: 4, e: 0, p: 0 },
    'Javelin':      { wac: 15, wdc: 5,  b: 0, e: 0, p: 6 },
    'Spear':        { wac: 20, wdc: 10, b: 4, e: 0, p: 7 },
    'Trident':      { wac: 20, wdc: 15, b: 0, e: 0, p: 5 },
    'Lance':        { wac: 25, wdc: 5,  b: 0, e: 4, p: 8 },
    // Polearm group
    'Glaive':       { wac: 25, wdc: 10, b: 6, e: 7, p: 6 },
    'Glaive/Bill':  { wac: 25, wdc: 10, b: 6, e: 7, p: 6 },
    'Poleaxe':      { wac: 25, wdc: 5,  b: 6, e: 6, p: 6 },
    'Pike':         { wac: 25, wdc: 5,  b: 0, e: 0, p: 8 },
    'Polearm':      { wac: 25, wdc: 10, b: 6, e: 6, p: 6 },
    // Net/Whip
    'Net':          { wac: 20, wdc: 0,  b: 0, e: 2, p: 0 },
    'Whip':         { wac: 25, wdc: 5,  b: 0, e: 2, p: 1 },
    'Isagra':       { wac: 25, wdc: 5,  b: 0, e: 3, p: 3 },
  }

  // Add occupation weapons from skill roster that aren't already in equipment
  // (e.g. Mace from weapon pool draw but equipment only has Broadsword)
  const existingWeaponNames = new Set(rows.map(r => (r.weapon_name as string).toLowerCase()))
  const occWeapons = (aging.occupationWeapons ?? []) as Array<{ specialty: string; group: string; omlMult: number }>
  for (const ow of occWeapons) {
    if (!ow.specialty) continue
    if (existingWeaponNames.has(ow.specialty.toLowerCase())) continue
    const ml = findML(ow.specialty, ow.group)
    if (ml <= 30) continue // skip if only at baseline
    const ws = WEAPON_STATS[ow.specialty] ?? WEAPON_STATS[ow.group] ?? { wac: 0, wdc: 0, b: 0, e: 0, p: 0 }
    rows.push({
      character_id: characterId,
      weapon_name: ow.specialty,
      ref_weapon_name: ow.specialty,  // specialty name IS the ref weapon name
      weapon_quality: 10,
      ml,
      attack_class: ws.wac,
      defence_class: ws.wdc,
      hand_mode_penalty: 0,
      impact_b: ws.b,
      impact_e: ws.e,
      impact_p: ws.p,
      weight_lbs: 0,
      is_equipped: true,
      is_primary: false,
    })
  }

  // Mark the primary weapon using hand slot data when available,
  // falling back to highest-ML melee weapon for occupation-only rows
  const primaryKey = hands?.right?.hm3_key ?? hands?.left?.hm3_key ?? null
  if (primaryKey) {
    const handRow = rows.find(r => (r as Record<string,unknown>).ref_weapon_name === primaryKey
                                || (r as Record<string,unknown>).held_in === 'right'
                                || (r as Record<string,unknown>).held_in === 'left')
    if (handRow) {
      handRow.is_primary = true
    }
  } else {
    // Fallback: highest-ML melee weapon (no hand slot data, e.g. occupation-only rows)
    const meleeRows = rows.filter(r => !(r as Record<string,unknown>).weapon_name?.toString().toLowerCase().includes('shield')
      && !(r as Record<string,unknown>).weapon_name?.toString().toLowerCase().includes('bow')
      && !(r as Record<string,unknown>).weapon_name?.toString().toLowerCase().includes('sling'))
    if (meleeRows.length > 0) {
      const best = meleeRows.reduce((a, b) => ((a.ml as number) >= (b.ml as number) ? a : b))
      best.is_primary = true
    } else if (rows.length > 0) {
      rows[0].is_primary = true
    }
  }

  return rows
}

// ─── Armour mapping — extracts armour from generator combat_gear ──────────

// Expand abbreviated coverage codes to full canonical location names
const COVERAGE_EXPAND: Record<string, string[]> = {
  Sk: ['Skull'],       Fa: ['Face'],        Nk: ['Neck'],
  Sh: ['Shoulder-L','Shoulder-R'],           Ua: ['Upper-Arm-L','Upper-Arm-R'],
  El: ['Elbow-L','Elbow-R'],                Fw: ['Forearm-L','Forearm-R'],
  Fo: ['Forearm-L','Forearm-R'],            // engine uses Fo, keep Fw for ref data compat
  Ha: ['Hand-L','Hand-R'],                  Tx: ['Thorax'],
  Ch: ['Thorax'],                           // breastplates — HM3 Thorax zone
  Bk: ['Thorax'],                           // backplates — same Thorax zone
  Ab: ['Abdomen'],     Gr: ['Groin'],
  Hp: ['Hip-L','Hip-R'],                    Th: ['Thigh-L','Thigh-R'],
  Kn: ['Knee-L','Knee-R'],                  Cf: ['Calf-L','Calf-R'],
  Ca: ['Calf-L','Calf-R'],                  // engine uses Ca, keep Cf for ref data compat
  Ft: ['Foot-L','Foot-R'],
}

function expandCoverage(codes: string[]): string[] {
  const result: string[] = []
  for (const code of codes) {
    const expanded = COVERAGE_EXPAND[code]
    if (expanded) result.push(...expanded)
    else result.push(code) // already a full name
  }
  return result
}

// Clothing name → armour coverage mapping (HM3: all clothing provides Cloth protection)
const CLOTHING_COVERAGE: Array<{ pattern: RegExp; coverage: string[]; weight: number; material?: string }> = [
  { pattern: /tunic|shirt|smock|doublet|jerkin|vest|robe|surcoat/i, coverage: ['Shoulder-L','Shoulder-R','Upper-Arm-L','Upper-Arm-R','Elbow-L','Elbow-R','Forearm-L','Forearm-R','Thorax','Abdomen','Hip-L','Hip-R','Groin'], weight: 4.4 },
  { pattern: /breeches|trousers|leggings|hose|skirt/i,             coverage: ['Groin','Hip-L','Hip-R','Thigh-L','Thigh-R','Knee-L','Knee-R','Calf-L','Calf-R'], weight: 4.4 },
  { pattern: /boot/i,                                                 coverage: ['Calf-L','Calf-R','Foot-L','Foot-R'], weight: 3.2, material: 'Leather' },
  { pattern: /shoe|sandal|clog|moccasin|foot.cloth/i,               coverage: ['Foot-L','Foot-R'], weight: 1.2, material: 'Leather' },
  { pattern: /cloak|cape|mantle/i,                                   coverage: ['Shoulder-L','Shoulder-R','Upper-Arm-L','Upper-Arm-R','Thorax','Abdomen'], weight: 5.3 },
  { pattern: /cap|hat|hood|cowl|coif/i,                             coverage: ['Skull'], weight: 0.8 },
  { pattern: /glove|gauntlet|mitten/i,                               coverage: ['Hand-L','Hand-R'], weight: 0.8, material: 'Leather' },
]

export function mapArmour(npc: GeneratorResult, characterId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const eq = (npc.equipment ?? (npc.aging as any)?.equipment) as Record<string, any> | null
  if (!eq) return []

  const rows: Array<Record<string, unknown>> = []

  // Combat armour pieces
  const armor = eq.combat_gear?.armor as Array<{
    name: string; hm3_key: string; material: string; coverage: string
    protection: string; weight_lb: number; cost_d: number
  }> | undefined
  if (armor) for (const a of armor) {
    const cov = a.coverage ? expandCoverage(a.coverage.split(/\s+/)) : []
    rows.push({
      character_id: characterId,
      item_name: a.name,
      ref_item_name: null,
      material_id: a.material ? a.material.charAt(0).toUpperCase() + a.material.slice(1) : 'Leather',
      quality_mod: 0,
      weight_lbs: a.weight_lb ?? 0,
      coverage: cov,
      is_worn: true,
    })
  }

  // Track locations already covered by combat armour (to avoid duplication with clothing)
  const combatCoveredLocations = new Set<string>()
  for (const r of rows) {
    for (const loc of (r.coverage as string[]) ?? []) combatCoveredLocations.add(loc)
  }

  // Clothing as Cloth armour (HM3: all clothing provides Cloth B1/E1/P1/F1)
  // Skip clothing items that overlap with combat armour (e.g. boots if combat gear has boots)
  const clothing = eq.clothing?.base_items as Array<{ name: string; cost_d?: number }> | undefined
  const coveredTypes = new Set<string>()
  if (clothing) for (const c of clothing) {
    const match = CLOTHING_COVERAGE.find(m => m.pattern.test(c.name))
    if (match) {
      // Skip if most coverage locations are already covered by combat armour
      // (e.g. heraldic surcoat from clothing when combat already has a surcoat)
      const coveredCount = match.coverage.filter(loc => combatCoveredLocations.has(loc)).length
      if (coveredCount >= match.coverage.length * 0.5) continue

      coveredTypes.add(match.pattern.source)
      // Heavy/hide/leather clothing is leather, not cloth
      const isLeather = /heavy|hide|leather/i.test(c.name)
      rows.push({
        character_id: characterId,
        item_name: c.name,
        ref_item_name: null,
        material_id: match.material ?? (isLeather ? 'Leather' : 'Cloth'),
        quality_mod: 0,
        weight_lbs: match.weight,
        coverage: match.coverage,
        is_worn: true,
      })
    }
  }

  // Ensure baseline clothing — every character wears at least a shirt, trousers, and shoes
  const hasShirt = clothing?.some(c => /tunic|shirt|smock|doublet|jerkin|vest|robe|surcoat/i.test(c.name))
  const hasTrousers = clothing?.some(c => /breeches|trousers|leggings|hose|skirt/i.test(c.name))
  const hasFootwear = rows.some(r => (r.coverage as string[])?.includes('Foot-L'))
  if (!hasShirt) {
    rows.push({
      character_id: characterId, item_name: 'Linen shirt', ref_item_name: null,
      material_id: 'Cloth', quality_mod: 0, weight_lbs: 2.0,
      coverage: ['Shoulder-L','Shoulder-R','Upper-Arm-L','Upper-Arm-R','Elbow-L','Elbow-R','Forearm-L','Forearm-R','Thorax','Abdomen','Hip-L','Hip-R','Groin'],
      is_worn: true,
    })
  }
  if (!hasTrousers) {
    rows.push({
      character_id: characterId, item_name: 'Wool trousers', ref_item_name: null,
      material_id: 'Cloth', quality_mod: 0, weight_lbs: 3.0,
      coverage: ['Groin','Hip-L','Hip-R','Thigh-L','Thigh-R','Knee-L','Knee-R','Calf-L','Calf-R'],
      is_worn: true,
    })
  }
  if (!hasFootwear) {
    rows.push({
      character_id: characterId, item_name: 'Leather shoes', ref_item_name: null,
      material_id: 'Leather', quality_mod: 0, weight_lbs: 1.2,
      coverage: ['Foot-L','Foot-R'],
      is_worn: true,
    })
  }

  return rows
}

// Canonical ref_skills names (from seed migration)
// Canonical skill names that match ref_skills in Supabase.
// normaliseSkillName() validates against this set.
// Includes: automatic skills (title case), weapon groups, weapon specialties,
// craft/communication skills, ritual variants, and parameterised base types.
const REF_SKILLS = new Set([
  // Automatic skills (engine outputs UPPERCASE, normalised to Title Case for DB)
  'Climbing','Condition','Jumping','Stealth','Throwing',
  'Awareness','Intrigue','Oratory','Rhetoric','Singing',
  'Initiative','Unarmed',
  // Armour skills (house rule — HM3_Armour_Skill_ClaudeCode_Spec.md)
  'Armour Light','Armour Mail','Armour Harness',
  // Physical / combat
  'Acrobatics','Dancing','Legerdemain','Lockcraft','Riding','Skiing','Swimming',
  'Acting','Lovecraft','Mental Conflict','Musician','Language','Script','Heraldry',
  // Weapon groups (13 RAW groups — now real skills in the engine)
  'Sword','Axe','Blowgun','Bow','Club','Dagger','Flail','Net','Polearm',
  'Shield','Sling','Spear','Whip',
  // Weapon specialties
  'Broadsword','Shortsword','Bastard Sword','Battlesword','Estoc','Falchion','Longknife',
  'Battleaxe','Handaxe','Warhammer',
  'Mace','Maul','Morningstar',
  'Javelin','Lance','Staff','Trident',
  'Taburi',
  'Flail','Warflail',
  'Shortbow','Longbow','Crossbow',
  'Poleaxe','Glaive/Bill',
  'Buckler',
  // Craft / lore skills
  'Agriculture','Alchemy','Animalcraft','Astrology','Brewing','Ceramics','Cookery','Drawing',
  'Dogcraft','Embalming','Engineering','Fishing','Fletching','Folklore','Foraging','Glassworking',
  'Herblore','Hidework','Horsecraft','Hunting','Inkcraft','Jewelcraft','Law','Lore',
  'Masonry','Mathematics','Metalcraft','Milling','Mining','Needlework',
  'Perfumery','Physician','Piloting','Ratcraft','Runecraft','Seamanship','Shipwright',
  'Survival','Tarotry','Textilecraft','Theology','Timbercraft','Tracking',
  'Weaponcraft','Weatherlore','Woodcraft',
  // Ritual variants (DB format: Ritual-{Deity})
  'Ritual-Agrik','Ritual-Halea','Ritual-Ilvir','Ritual-Larani','Ritual-Morgath',
  'Ritual-Naveh','Ritual-Peoni','Ritual-Sarajin','Ritual-Save-Knor','Ritual-Siem',
  // Convocation / Psionic (future)
  'Convocation-Lyahvi','Convocation-Peleahn','Convocation-Jmorvi','Convocation-Fyvria',
  'Convocation-Savorya','Convocation-Neutral','Psionic-Talent',
])

// Maps non-canonical engine names → canonical ref_skills names.
// Many entries removed in v3.13.2: engine now outputs canonical names from hm3-skills.json.
// Remaining entries handle: legacy weapon variants, occupation aliases, bare language names.
// Legacy name mappings — only for names that can't be in ref_skills.
// Most entries removed in v3.10.0–v3.13.4 as engine now outputs canonical names.
const SKILL_NAME_MAP: Record<string, string> = {
  // Rare weapon variants not generated by the engine, safety net for manual characters
  'Rapier': 'Sword', 'Scimitar': 'Sword',
  'Jousting Lance': 'Lance', 'Kiteshield': 'Shield',
  // Skill alias — Administration uses the Law formula
  'Administration': 'Law',
}

function normaliseSkillName(name: string, deity?: string | null): string | null {
  // Ritual-Larani format — already canonical, pass through
  if (name.startsWith('Ritual-')) {
    return REF_SKILLS.has(name) ? name : null
  }
  // Legacy: bare Ritual → Ritual-{Deity} (pre-v3.12.0 data)
  if (name === 'Ritual' || name === 'Theology') {
    return deity ? `Ritual-${deity}` : 'Ritual-Peoni'
  }

  // Strip parenthetical variants: "Language (Emela)" → "Language", "Script (Lakise)" → "Script"
  const base = name.replace(/\s*\(.*\)$/, '')

  // Check explicit map for legacy aliases
  if (SKILL_NAME_MAP[base]) return SKILL_NAME_MAP[base]
  if (SKILL_NAME_MAP[name]) return SKILL_NAME_MAP[name]

  // Canonical match (engine now outputs Title Case for automatic skills)
  if (REF_SKILLS.has(base)) return base

  // Unknown skill — skip it rather than crash the insert
  return null
}

// ─── Skill mapping — reads pre-computed values from engine's skill roster ───

export function mapSkills(npc: GeneratorResult, characterId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const aging = (npc.aging ?? npc) as any
  const deity = (npc.publicDeity ?? aging.publicDeity ?? null) as string | null
  const hobbySkill = (aging.hobbySkill ?? null) as string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const skills: any[] = []
  const seen = new Set<string>()

  const hobbyNormalised = hobbySkill ? normaliseSkillName(hobbySkill, deity) : null

  // Build roster lookup: engine skill name → { sb, oml, tier, source }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const roster: Record<string, { sb: number; oml: number; tier: string; source: string; skillGroup?: string | null }> = {}
  for (const entry of (aging.skillRoster ?? [])) {
    const name = normaliseSkillName(entry.name, deity)
    if (name) roster[name] = { sb: entry.sb, oml: entry.oml, tier: entry.tier, source: entry.source, skillGroup: entry.skillGroup }
  }

  // Build OP gains lookup: normalised skill name → total ML gain from advancement
  const opsMap: Record<string, number> = {}
  for (const [rawName, ops] of Object.entries(aging.skillImprovements ?? {})) {
    const name = normaliseSkillName(rawName, deity)
    if (name) opsMap[name] = (opsMap[name] ?? 0) + (ops as number)
  }

  // Collect all skill names from both roster and improvements
  const allSkillNames = new Set<string>()
  for (const name of Object.keys(roster)) allSkillNames.add(name)
  for (const name of Object.keys(opsMap)) allSkillNames.add(name)

  // Weapon skills that exist only as baseline entries (source 'weapon' or 'weapon_fallback')
  // should be excluded unless the character actually trained them (has OP advancement).
  // These are registered for every character by the skill book but aren't "known" skills.
  const BASELINE_SOURCES = new Set(['weapon', 'weapon_fallback', 'weapon_group', 'hobby_pool', 'additional'])

  // Build set of weapon skills the character actually carries in equipment —
  // these should never be filtered even if they have 0 OP (e.g. shortbow in pack)
  const equippedWeaponSkills = new Set<string>()
  const eq = (npc.equipment ?? (npc.aging as any)?.equipment) as Record<string, any> | null
  if (eq) {
    const EQUIP_TO_SKILL: Record<string, string> = {
      shortbow: 'Shortbow', longbow: 'Longbow', crossbow: 'Crossbow',
      dagger: 'Dagger', knife: 'Dagger', staff: 'Staff', club: 'Club',
      mace: 'Mace', spear: 'Spear', sling: 'Sling',
      broadsword: 'Broadsword', shortsword: 'Shortsword', falchion: 'Falchion',
      handaxe: 'Handaxe', battleaxe: 'Battleaxe', halberd: 'Polearm',
    }
    // Check combat gear weapons + all loadout weapons
    const allWeaponNames: string[] = []
    for (const w of (eq.combat_gear?.weapons ?? [])) allWeaponNames.push(w.name ?? w.hm3_key ?? '')
    for (const w of (eq.combat_gear?.ranged ?? [])) allWeaponNames.push(w.name ?? w.hm3_key ?? '')
    for (const loadout of Object.values(eq.loadouts ?? {})) {
      for (const w of ((loadout as any)?.weapons ?? [])) allWeaponNames.push(typeof w === 'string' ? w : w.name ?? '')
    }
    // Also check profession tools and general items (shortbow, sling, etc.)
    for (const item of (eq.profession_tools?.items ?? [])) allWeaponNames.push(item.name ?? '')
    for (const item of (eq.general_items ?? [])) allWeaponNames.push(item.name ?? '')
    for (const wn of allWeaponNames) {
      const lower = wn.toLowerCase()
      for (const [keyword, skill] of Object.entries(EQUIP_TO_SKILL)) {
        if (lower.includes(keyword)) { equippedWeaponSkills.add(skill); break }
      }
    }
  }

  for (const skillName of allSkillNames) {
    if (seen.has(skillName)) continue
    seen.add(skillName)

    const r = roster[skillName]
    const sb = r?.sb ?? 10
    const oml = r?.oml ?? sb * 3
    const opsSpent = opsMap[skillName] ?? 0
    const ml = oml + opsSpent

    // Skip baseline weapon entries that the character never trained or used
    // BUT keep skills for weapons the character actually carries
    if (opsSpent === 0 && r && BASELINE_SOURCES.has(r.source) && !equippedWeaponSkills.has(skillName)) continue

    const isAutoOnly = opsSpent === 0
    const isHobby = hobbyNormalised === skillName

    // Determine source for DB: roster tier → DB source label
    let source: string
    if (isAutoOnly) {
      source = 'automatic'
    } else if (isHobby) {
      source = 'optional'
    } else {
      source = 'occupational'
    }

    skills.push({
      character_id: characterId,
      skill_name: skillName,
      is_automatic: isAutoOnly,
      sb,
      ml: Math.min(sb + 100, ml),   // HM3 max ML = SB + 100
      oml: Math.min(sb + 100, oml),
      source,
      notes: isHobby ? 'Hobby' : null,
      weapon_group: r?.skillGroup ?? null,
    })
  }

  return skills
}

// ─── Helpers ────────────────────────────────────────────────────────────────

// Clamp seed to Postgres INTEGER range (max 2,147,483,647)
function clampSeed(seed: number | null | undefined): number | null {
  if (seed == null) return null
  return seed > 2147483647 ? seed % 2147483647 : seed
}

const MONTH_NAMES: Record<string, number> = {
  nuzyael: 1, peonu: 2, kelen: 3, nolus: 4, larane: 5, agrazhar: 6,
  azura: 7, halane: 8, savor: 9, ilvin: 10, navek: 11, morgat: 12,
}

function monthNameToNumber(name: string | number | null | undefined): number | null {
  if (name == null) return null
  if (typeof name === 'number') return name >= 1 && name <= 12 ? name : null
  const n = MONTH_NAMES[name.toLowerCase()]
  return n ?? null
}

function mapSunsign(sunsign: string | null | undefined): string | null {
  if (!sunsign) return null
  // Generator uses same names as ref_sunsigns: Ulandus, Aralius, etc.
  // Capitalize first letter to match DB
  return sunsign.charAt(0).toUpperCase() + sunsign.slice(1)
}

// Map engine occupation names to ref_occupations Title Case values
// OCCUPATION_MAP removed in v3.13.4 — engine now provides occupationLabel from OCCUPATION_LABELS.
// Fallback for legacy data without occupationLabel:
function mapOccupation(occ: string | null): string | null {
  if (!occ) return null
  return occ.charAt(0).toUpperCase() + occ.slice(1)
}

function mapCulture(npc: GeneratorResult): string {
  // Generator's culture field or infer from class
  if (npc.culture === 'pagaelin') return 'Tribal'
  if (npc.tribalAlignment) return 'Tribal'
  return 'Feudal' // default for Kaldor
}
